import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, themeQuartz } from 'ag-grid-community';
import '../../config/agGridConfig'; // Register AG Grid modules
import { adminAPI } from '../../services/api';
import '../../styles/AdminGlobal.css';
import './Admin.css';
import FacebookPixelFormModal from '../../components/admin/FacebookPixelFormModal';

const FacebookPixelManagement: React.FC = () => {
  const [pixels, setPixels] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPixel, setEditingPixel] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);

  const columnDefs: ColDef[] = [
    { field: 'id', headerName: 'ID', width: 70, sortable: true },
    { field: 'pixel_id', headerName: 'Pixel ID', sortable: true, filter: true, flex: 1 },
    { 
      field: 'is_active', 
      headerName: 'Status', 
      width: 120,
      cellRenderer: (params: any) => (
        <span className={`status-badge ${params.value ? 'approved' : 'pending'}`}>
          {params.value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    { 
      field: 'created_at', 
      headerName: 'Created', 
      sortable: true, 
      width: 150,
      valueFormatter: (p: any) => new Date(p.value).toLocaleDateString()
    },
    { 
      field: 'updated_at', 
      headerName: 'Last Updated', 
      sortable: true, 
      width: 150,
      valueFormatter: (p: any) => new Date(p.value).toLocaleDateString()
    },
    { 
      headerName: 'Actions',
      width: 100,
      pinned: 'right',
      lockPosition: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const buttonStyle = {
          border: 'none',
          borderRadius: '6px',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '16px',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        };
        
        return (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <button 
              onClick={() => handleEditPixel(params.data)}
              title="Edit Facebook Pixel"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#007bff',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              ✏️
            </button>
            <button 
              onClick={() => handleDeletePixel(params.data.id)}
              title="Delete Facebook Pixel"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#dc3545',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              🗑️
            </button>
          </div>
        );
      }
    }
  ];

  useEffect(() => {
    loadPixels();
  }, []);

  const loadPixels = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getFacebookPixel();
      let data = response.data || response;
      
      // Handle paginated response
      if (data && data.results && Array.isArray(data.results)) {
        data = data.results;
      }

      // Facebook Pixel might return a single object or array
      const pixelsList = Array.isArray(data) ? data : (data ? [data] : []);
      setPixels(pixelsList);
    } catch (error) {
      console.error('Failed to load Facebook Pixel:', error);
      setPixels([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPixel = () => {
    setEditingPixel(null);
    setShowModal(true);
  };

  const handleEditPixel = (pixel: any) => {
    setEditingPixel(pixel);
    setShowModal(true);
  };

  const handleDeletePixel = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this Facebook Pixel?')) {
      return;
    }

    try {
      await adminAPI.deleteFacebookPixel(id);
      await loadPixels();
      alert('Facebook Pixel deleted successfully!');
    } catch (error: any) {
      console.error('Failed to delete pixel:', error);
      alert(error.response?.data?.message || 'Failed to delete Facebook Pixel');
    }
  };

  const handleSubmit = async (data: any) => {
    try {
      setSubmitting(true);
      
      if (editingPixel) {
        // Update existing pixel
        await adminAPI.updateFacebookPixel(editingPixel.id, data);
        alert('Facebook Pixel updated successfully!');
      } else {
        // Create new pixel
        await adminAPI.createFacebookPixel(data);
        alert('Facebook Pixel created successfully!');
      }
      
      setShowModal(false);
      setEditingPixel(null);
      await loadPixels();
    } catch (error: any) {
      console.error('Failed to save pixel:', error);
      alert(error.response?.data?.message || 'Failed to save Facebook Pixel');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPixel(null);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Ensure pixels is always an array before filtering
  const pixelsArray = Array.isArray(pixels) ? pixels : [];
  const filteredPixels = pixelsArray.filter(pixel =>
    pixel.pixel_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-page">
      <div className="management-header">
        <div>
          <h3>Facebook Pixel Management</h3>
          <p>Configure Facebook Pixel for tracking and analytics</p>
        </div>
        <div className="management-actions">
          <button className="btn btn-success" onClick={handleAddPixel}>
            + Add Pixel
          </button>
          <button className="btn btn-secondary" onClick={loadPixels}>
            🔄 Refresh
          </button>
        </div>
      </div>
      
      <div className="ag-grid-wrapper">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by Pixel ID..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        <div className="results-info">
          <span>{filteredPixels.length}</span> pixel(s) configured
        </div>
      
        <div className="ag-grid-container">
          <AgGridReact

            theme={themeQuartz}
            rowData={filteredPixels || []}
            columnDefs={columnDefs}
            pagination={true}
            paginationPageSize={20}
            loading={loading}

            defaultColDef={{
              resizable: true,
            }}
          />
        </div>
      </div>

      <FacebookPixelFormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        initialData={editingPixel}
        loading={submitting}
      />
    </div>
  );
};

export default FacebookPixelManagement;
